import { useCallback, useState } from 'react';

import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { normalizeDateString } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';

import {
  IEditAllocationFormValues,
  NormalizedTeamMembersAllocations,
} from '../../../shared/model/types';
import { IAddClientAllocationPayload } from '~/services/api/clientAllocation/types';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import { groupAllocationsToAdd } from '../../utils';

const buildPayloadForAllocation = (
  teamMembersAllocations: NormalizedTeamMembersAllocations[],
  allocationId: number,
) => {
  const member = teamMembersAllocations.find(m =>
    m.clientsAllocations.some(
      c => c.allocation?.id === allocationId || c.allocationId === allocationId,
    ),
  );

  if (!member) return null;

  const clients = member.clientsAllocations.flatMap(client =>
    client.allocation?.id === allocationId || client.allocationId === allocationId
      ? [
          {
            id: client.clientId,
            memo: client.memo,
            teamId: client.teamId ? Number(client.teamId) : undefined,
          },
        ]
      : [],
  );

  const locationGroupIds = member.locationGroupAllocations.reduce<number[]>((acc, lg) => {
    if (lg.allocation?.id === allocationId) acc.push(lg.locationGroupId);
    return acc;
  }, []);

  return { clients, locationGroupIds };
};

const useEditAllocationSubmit = () => {
  const { actTeamId, allocationDate, clinicId } = useEditClientAllocationContext();
  const { isGlobalUser, isActTeamMember } = useRole();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useStoreActions(actions => actions.snackbar);
  const onAddOwnClientAllocationGU = useStoreActions(
    actions => actions.clientAllocation.onAddOwnClientAllocationGU,
  );
  const onAddClientAllocation = useStoreActions(
    actions => actions.clientAllocation.onAddClientAllocation,
  );

  const submit = useCallback(
    async (values: IEditAllocationFormValues): Promise<boolean> => {
      const { changedAllocationsIds, deletedAllocationsIds, teamMembersAllocations } = values;
      const allocationsForAdd = groupAllocationsToAdd(teamMembersAllocations);

      if (
        !allocationsForAdd.length &&
        !changedAllocationsIds.length &&
        !deletedAllocationsIds.length
      ) {
        return false;
      }
      setIsSubmitting(true);

      const datePayload = {
        from: normalizeDateString(allocationDate),
        to: normalizeDateString(allocationDate, true),
        includeWeekends: true as const,
      };

      try {
        if (allocationsForAdd.length) {
          const addMethod = isGlobalUser ? onAddOwnClientAllocationGU : onAddClientAllocation;

          await addMethod({
            clinicId,
            teamId: actTeamId,
            payload: { ...datePayload, allocations: allocationsForAdd },
          } as IAddClientAllocationPayload);
        }

        if (deletedAllocationsIds.length) {
          await Promise.all(
            deletedAllocationsIds.map(allocationId => {
              if (isGlobalUser) {
                return api.clientAllocation.deleteOwnClientAllocationGU({
                  clinicId,
                  clientAllocationId: String(allocationId),
                });
              }
              if (isActTeamMember) {
                return api.clientAllocation.deleteOwnClientAllocation({
                  clinicId,
                  teamId: actTeamId,
                  clientAllocationId: String(allocationId),
                });
              }
              return api.clientAllocation.deleteClientAllocation({
                clinicId,
                teamId: actTeamId,
                clientAllocationId: String(allocationId),
              });
            }),
          );
        }

        if (changedAllocationsIds.length) {
          if (isGlobalUser) {
            await Promise.all(
              changedAllocationsIds.map(allocationId => {
                const payload = buildPayloadForAllocation(teamMembersAllocations, allocationId);

                if (!payload) return Promise.resolve();

                return api.clientAllocation.updateOwnClientAllocationGU(
                  { clinicId, clientAllocationId: String(allocationId) },
                  {
                    ...datePayload,
                    clients: payload.clients,
                    locationGroupIds: payload.locationGroupIds,
                  },
                );
              }),
            );
          } else if (isActTeamMember) {
            const [allocationId] = changedAllocationsIds;
            const payload = buildPayloadForAllocation(teamMembersAllocations, allocationId);
            if (payload) {
              await api.clientAllocation.updateOwnClientAllocation(
                { clinicId, teamId: actTeamId, clientAllocationId: String(allocationId) },
                {
                  ...datePayload,
                  clients: payload.clients,
                  locationGroupIds: payload.locationGroupIds,
                } as any,
              );
            }
          } else {
            const allocations = changedAllocationsIds.flatMap(allocationId => {
              const payload = buildPayloadForAllocation(teamMembersAllocations, allocationId);
              if (!payload) return [];
              return [
                {
                  id: String(allocationId),
                  clients: payload.clients,
                  locationGroupIds: payload.locationGroupIds,
                },
              ];
            });
            if (allocations.length) {
              await api.clientAllocation.updateClientAllocation(
                { clinicId, teamId: actTeamId },
                { allocations },
              );
            }
          }
        }

        return true;
      } catch (error) {
        showError(extractErrorMessage(error));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      actTeamId,
      allocationDate,
      clinicId,
      isGlobalUser,
      isActTeamMember,
      showError,
      onAddOwnClientAllocationGU,
      onAddClientAllocation,
    ],
  );

  return { submit, isSubmitting };
};

export default useEditAllocationSubmit;
